/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { KDialog, KTextInput, KSelectMulti } from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSetState";
import { getVehiclesNew as getVehicles } from "../../apiCalls/admin/vehicles";
import { getUsers } from "../../apiCalls/admin/users";
import { createVehicleGroup } from "../../apiCalls/admin/vehicleGroups";
import { setAlert } from "../../redux/actions/actions";
import KSelectList from "../../components/ui/KSelectList";
import { DropDownType } from "../../interfaces/DropDownInterface";
import { UserInterface } from "../../interfaces/UserInterface";

interface Props {
  open: boolean;
  handleClose: (reload: boolean) => void;
}

interface SelectListProps {
  selectedUsers: UserInterface[];
  dropDownItems: DropDownType[];
  setSelectedUserId: (id: number) => void;
  setSelectedUsers: (value: UserInterface, checkbox: string) => void;
}

const MemoziedSelectList = React.memo(
  (props: SelectListProps) => (
    <KSelectList
      elements={props.selectedUsers.map(
        (x) => `${x.first_name} ${x.last_name}`,
      )}
      data={props.selectedUsers}
      width="auto"
      height={200}
      dropDownItems={props.dropDownItems}
      setSelectedUserId={props.setSelectedUserId}
      setSelectedUsers={props.setSelectedUsers}
    />
  ),
  (oldProps, newProps) => {
    return oldProps.selectedUsers === newProps.selectedUsers;
  },
);

const NewVehicleGroup: React.FC<Props> = ({ open, handleClose }: Props) => {
  const currentCustomer = useSelector(
    (state: any) => state.customer.currentCustomer,
  );
  const dispatch = useDispatch();

  const [
    { name, users, vehicles, selectedUsers, selectedVehicles },
    setState,
  ] = useSetState({
    name: "",
    users: [],
    vehicles: [],
    selectedUsers: [],
    selectedVehicles: [],
  });

  const setSelectedUsers = (value: UserInterface, checkbox: string): void => {
    const newList: UserInterface[] = selectedUsers;
    const userToUpdate: UserInterface = newList?.filter(
      (x) => x.user_id === value.user_id,
    )[0];
    userToUpdate[checkbox] = !userToUpdate[checkbox];
    setState({ selectedUsers: [...newList] });
  };

  const dropDownItems: DropDownType[] = [
    {
      icon: "edit",
      name: "edit_vehicle",
      title: "Edit",
    },
    {
      icon: "calendar",
      name: "create_reservation",
      title: "Reserve",
    },
  ];

  const handleChange = (e): void =>
    setState({ [e.target.name]: e.target.value });

  const setSelectedUserId = (id: number): void => {
    setState({ selectedUserId: id });
  };

  const closePopup = (reload: boolean) => {
    handleClose(reload);
    setState({
      name: "",
      users: [],
      vehicles: [],
      selectedUsers: [],
      selectedVehicles: [],
    });
  };

  const handleInfiniteSelectChange = (name, value): void => {
    setState({ selectedVehicles: value.map((x) => x.label) });
  };

  const handleUserInfiniteSelectChange = (name, value, option): void => {
    const currentOption: UserInterface = option.option;
    const currentUserId: string[] = selectedUsers.map(
      (x: UserInterface) => x.user_id,
    );
    if (currentUserId.includes(currentOption.user_id)) {
      const usersAfterRemoved: UserInterface = selectedUsers.filter(
        (x: UserInterface) => x.user_id !== currentOption.user_id,
      );
      setState({
        selectedUsers: usersAfterRemoved,
      });
    } else {
      setState({
        selectedUsers: [
          ...selectedUsers,
          ...[currentOption].map((x) => ({
            user_id: x.user_id,
            first_name: x.first_name || "",
            last_name: x.last_name || "",
            avatar: x.avatar || "",
            edit_vehicle: x.edit_vehicle || false,
            create_reservation: x.create_reservation || true,
          })),
        ],
      });
    }
  };

  const handleCreateVehicleGroup = (): void => {
    createVehicleGroup(
      currentCustomer?.shortname,
      name,
      selectedUsers.map((x) => ({
        user_id: x.user_id,
        edit_vehicle: x.edit_vehicle,
        create_reservation: x.create_reservation,
      })),
      selectedVehicles,
    ).then((res: any) => {
      if (res.data) {
        dispatch(setAlert("Vehicle group created", "success"));
        closePopup(true);
      } else {
        dispatch(setAlert(Object.values(res.errors[0]), "error"));
      }
    });
  };

  useEffect(() => {
    if (open) {
      getVehicles(currentCustomer?.shortname, "", 1, 2000).then((res) =>
        setState({
          vehicles: res?.data?.data?.map((x) => ({
            label: x?._id,
            name: x?.name,
          })),
        }),
      );
    }
  }, [currentCustomer, setState, open]);

  useEffect(() => {
    if (open) {
      getUsers(currentCustomer?.shortname).then((res) =>
        setState({
          users: res?.map((x) => ({
            user_id: x._id,
            first_name: x?.first_name || "",
            last_name: x?.last_name || "",
            avatar: x.avatar,
            name: `${x?.first_name} ${x?.last_name}` || "",
          })),
        }),
      );
    }
  }, [currentCustomer, setState, open]);

  return (
    <KDialog
      open={open}
      title="New Vehicle Group"
      size="xs"
      onClose={() => closePopup(false)}
      primaryCTAonClick={() => handleCreateVehicleGroup()}
      primaryCTAText="Create"
      primaryCTAColor="primary"
      secondaryCTAonClick={() => closePopup(false)}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={!name || !selectedUsers.length || !selectedVehicles.length}
    >
      <div style={{ minHeight: "400px" }}>
        <KTextInput
          label="Name"
          placeholder="Name of vehicle group"
          value={name}
          name="name"
          onChange={handleChange}
          width="100%"
        />
        <KSelectMulti
          onChange={handleInfiniteSelectChange}
          name="selectedVehicles"
          label="Vehicles"
          elements={vehicles}
          width="100%"
        />
        <KSelectMulti
          prefix="avatar"
          onChange={handleUserInfiniteSelectChange}
          name="selectedUsers"
          label="Users"
          elements={users}
          width="100%"
        />
        <MemoziedSelectList
          selectedUsers={selectedUsers}
          dropDownItems={dropDownItems}
          setSelectedUserId={setSelectedUserId}
          setSelectedUsers={setSelectedUsers}
        />
      </div>
    </KDialog>
  );
};

export default NewVehicleGroup;
